import { z } from 'zod';

const TicketPrioritySchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'URGENT']);
const SupportTicketStatusSchema = z.enum([
  'OPEN',
  'IN_PROGRESS',
  'WAITING_CUSTOMER',
  'RESOLVED',
  'CLOSED',
]);

const SenderTypeSchema = z.enum(['CUSTOMER', 'STAFF', 'SYSTEM']);

const BaseSupportTicketSchema = z.object({
  id: z.string(),
  ticketNumber: z.string(),
  customerId: z.string(),
  customerEmail: z.string().email(),
  customerName: z.string(),
  category: z.string(),
  subject: z.string(),
  description: z.string(),
  priority: TicketPrioritySchema,
  status: SupportTicketStatusSchema,
  assignedTo: z.string().nullable(),
  relatedOrderId: z.string().nullable(),
  relatedTicketId: z.string().nullable(),
  resolvedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const TicketMessageSchema = z.object({
  id: z.string(),
  ticketId: z.string(),
  senderId: z.string(),
  senderType: SenderTypeSchema,
  senderName: z.string(),
  content: z.string(),
  isInternal: z.boolean(),
  createdAt: z.string().datetime(),
});

export const SupportTicketCategorySchema = z.enum([
  'BOOKING',
  'PAYMENT',
  'REFUND',
  'WRISTBAND',
  'ATTRACTION',
  'FOOD',
  'GENERAL',
  'COMPLAINT',
  'FEEDBACK',
]);
export type SupportTicketCategory = z.infer<typeof SupportTicketCategorySchema>;

export const AdminSupportTicketSchema = BaseSupportTicketSchema.extend({
  messagesCount: z.number().int().optional(),
});
export type AdminSupportTicket = z.infer<typeof AdminSupportTicketSchema>;

export const AdminSupportTicketDetailSchema = AdminSupportTicketSchema.extend({
  messages: z.array(TicketMessageSchema),
});
export type AdminSupportTicketDetail = z.infer<typeof AdminSupportTicketDetailSchema>;

export const AdminSupportTicketsQuerySchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  status: SupportTicketStatusSchema.optional(),
  priority: TicketPrioritySchema.optional(),
  category: SupportTicketCategorySchema.optional(),
  assignedTo: z.string().optional(),
  search: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});
export type AdminSupportTicketsQuery = z.infer<typeof AdminSupportTicketsQuerySchema>;

export const UpdateSupportTicketSchema = z.object({
  status: SupportTicketStatusSchema.optional(),
  priority: TicketPrioritySchema.optional(),
  assignedTo: z.string().optional(),
});
export type UpdateSupportTicketInput = z.infer<typeof UpdateSupportTicketSchema>;

export const AddTicketMessageSchema = z.object({
  ticketId: z.string(),
  content: z.string().min(1).max(5000),
  isInternal: z.boolean().optional(),
});
export type AddTicketMessageInput = z.infer<typeof AddTicketMessageSchema>;

export const CreateCannedResponseSchema = z.object({
  title: z.string().min(1).max(100),
  content: z.string().min(1).max(5000),
  category: z.string().optional(),
  shortcut: z.string().optional(),
});
export type CreateCannedResponseInput = z.infer<typeof CreateCannedResponseSchema>;

export const UpdateCannedResponseSchema = CreateCannedResponseSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdateCannedResponseInput = z.infer<typeof UpdateCannedResponseSchema>;

export const AdminSupportTicketsResponseSchema = z.object({
  items: z.array(AdminSupportTicketSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type AdminSupportTicketsResponse = z.infer<typeof AdminSupportTicketsResponseSchema>;
